import { defaultsDeep, isEqual, isPlainObject } from 'lodash';
import { AgentUpdatedField, SourceLocator } from '~/types/AgentActivityLog';
import {
  EXTRA_SUBTREES_BY_PAYLOAD_KEY,
  REVIEW_PAYLOAD_KEYS,
  SOURCE_LOCATORS,
} from '~/constants/agentReview';

const seedOldSubtree = (
  oldSubtree: any,
  newSubtree: any,
  locator: SourceLocator | undefined,
  oldPayload: any
): any => {
  if (!locator) return oldSubtree;
  if (!isPlainObject(newSubtree)) return oldSubtree;

  let seeded: Record<string, any> = { ...(oldSubtree ?? {}) };

  if (locator.source) {
    const source = locator.source(oldPayload) ?? {};
    Object.keys(newSubtree).forEach((k) => {
      const sourceValue = source[locator.fieldMap[k] ?? k];
      if (seeded[k] !== undefined) {
        if (isPlainObject(seeded[k]) && isPlainObject(sourceValue)) {
          seeded[k] = defaultsDeep({}, seeded[k], sourceValue);
        }
        return;
      }
      seeded[k] = sourceValue ?? null;
    });
  }

  (locator.arrays ?? []).forEach((arr) => {
    const newArr: any[] = newSubtree[arr.arrayKey] ?? [];
    if (!Array.isArray(newArr) || !newArr.length) return;
    const sourceArr: any[] = arr.sourceArray(oldPayload) ?? [];
    const sourceByUid = new Map<string, any>();
    sourceArr.forEach((it) => it?.uid && sourceByUid.set(it.uid, it));

    const oldArr: any[] = (seeded[arr.arrayKey] as any[]) ?? [];
    const oldByUid = new Map<string, any>();
    oldArr.forEach((it) => it?.uid && oldByUid.set(it.uid, it));

    const seededArr = newArr.map((nItem) => {
      const seededItem: Record<string, any> = { ...(oldByUid.get(nItem.uid) ?? {}) };
      const sourceItem = sourceByUid.get(nItem.uid) ?? {};
      if (arr.personFieldMap && isPlainObject(nItem.personalData)) {
        const seededPd: Record<string, any> = { ...(seededItem.personalData ?? {}) };
        Object.keys(nItem.personalData).forEach((k) => {
          if (seededPd[k] !== undefined) return;
          const sourceKey = arr.personFieldMap![k];
          if (sourceKey && sourceItem.personalData?.[sourceKey] !== undefined) {
            seededPd[k] = sourceItem.personalData[sourceKey];
          } else {
            seededPd[k] = nItem.personalData[k];
          }
        });
        seededItem.personalData = seededPd;
      }
      seededItem.uid = nItem.uid;
      return seededItem;
    });

    seeded = { ...seeded, [arr.arrayKey]: seededArr };
  });

  return seeded;
};

const pruneToShape = (oldVal: any, newVal: any): any => {
  if (newVal === undefined) return undefined;

  if (Array.isArray(newVal)) {
    const o = Array.isArray(oldVal) ? oldVal : [];
    const allHaveUid =
      newVal.every((it) => it && typeof it === 'object' && 'uid' in it) &&
      o.every((it) => it && typeof it === 'object' && 'uid' in it);
    if (!allHaveUid) return oldVal ?? null;
    const oldByUid = new Map<string, any>();
    o.forEach((it) => oldByUid.set(it.uid, it));
    return newVal.map((nItem) => {
      const matched = oldByUid.get(nItem.uid);
      const pruned = pruneToShape(matched, nItem);
      if (isPlainObject(pruned)) {
        return { uid: nItem.uid, ...(pruned as Record<string, any>) };
      }
      return { uid: nItem.uid };
    });
  }

  if (isPlainObject(newVal)) {
    const out: Record<string, any> = {};
    Object.keys(newVal as Record<string, any>).forEach((k) => {
      const v = pruneToShape(
        (oldVal as Record<string, any>)?.[k],
        (newVal as Record<string, any>)[k]
      );
      if (v !== undefined) out[k] = v;
    });
    return out;
  }

  return oldVal ?? null;
};

export const buildUpdatedFieldsDiff = (
  oldPayload: any,
  newValues: any,
  payloadKey: string
): AgentUpdatedField[] => {
  if (!REVIEW_PAYLOAD_KEYS.includes(payloadKey)) return [];

  const oldData: Record<string, any> = {};
  const newData: Record<string, any> = {};
  let hasChange = false;

  const newPrimary = newValues?.[payloadKey];
  if (newPrimary != null) {
    const seededOld = seedOldSubtree(
      oldPayload?.[payloadKey],
      newPrimary,
      SOURCE_LOCATORS[payloadKey],
      oldPayload
    );
    const prunedOld = pruneToShape(seededOld, newPrimary);
    if (!isEqual(prunedOld, newPrimary)) {
      oldData[payloadKey] = prunedOld;
      newData[payloadKey] = newPrimary;
      hasChange = true;
    }
  }

  const extraKeys = EXTRA_SUBTREES_BY_PAYLOAD_KEY[payloadKey] ?? [];
  extraKeys.forEach((extraKey) => {
    const newExtra = newValues?.[extraKey];
    if (newExtra == null) return;
    const prunedOldExtra = pruneToShape(oldPayload?.[extraKey], newExtra);
    if (!isEqual(prunedOldExtra, newExtra)) {
      oldData[extraKey] = prunedOldExtra;
      newData[extraKey] = newExtra;
      hasChange = true;
    }
  });

  if (!hasChange) return [];

  return [{ oldData, newData }];
};
